import { translations } from '../i18n/translations.js';

const LANG_STORAGE_KEY = 'pixelblob_lang';

export default class I18nManager {
    constructor() {
        this.translations = translations;
        this.defaultLang = 'en';
        this.lang = this.detectInitialLanguage();
        this.instructionObserver = null;
    }

    detectInitialLanguage() {
        const saved = localStorage.getItem(LANG_STORAGE_KEY) || localStorage.getItem('quicky_lang');
        if (saved && this.translations[saved]) return saved;

        const browser = (navigator.language || 'en').slice(0, 2).toLowerCase();
        if (this.translations[browser]) return browser;
        return this.defaultLang;
    }

    getLanguage() {
        return this.lang;
    }

    setLanguage(lang) {
        if (!this.translations[lang]) return;
        this.lang = lang;
        localStorage.setItem(LANG_STORAGE_KEY, lang);
        document.documentElement.lang = lang;
        this.applyDomTranslations();
    }

    t(key, params = {}) {
        const fallback = params.defaultName ?? key;
        const value = this.lookup(key, this.lang) ?? this.lookup(key, this.defaultLang) ?? fallback;
        return this.interpolate(value, params);
    }

    lookup(key, lang) {
        const dict = this.translations[lang];
        if (!dict) return null;
        return key.split('.').reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : null), dict);
    }

    interpolate(str, params) {
        if (typeof str !== 'string') return str;
        return str.replace(/\{(\w+)\}/g, (_, name) => (params[name] ?? `{${name}}`));
    }

    applyDomTranslations(root = document) {
        const nodes = root.querySelectorAll('[data-i18n]');
        nodes.forEach((node) => {
            const key = node.getAttribute('data-i18n');
            if (!key) return;
            node.innerHTML = this.t(key);
        });

        const placeholderNodes = root.querySelectorAll('[data-i18n-placeholder]');
        placeholderNodes.forEach((node) => {
            const key = node.getAttribute('data-i18n-placeholder');
            if (!key) return;
            node.setAttribute('placeholder', this.t(key));
        });
    }

    translateKnownText(text) {
        if (!text || typeof text !== 'string') return text;
        const source = text.trim();

        const directMap = {
            'PLAY AS GUEST': 'landing.playAsGuest',
            'SIGN IN': 'landing.signIn',
            'PLAY': 'auth.play',
            'SHOP': 'auth.shop',
            'LEADERBOARDS': 'auth.leaderboards',
            'MY PROFILE': 'auth.myProfile',
            'FRIENDS': 'auth.friends',
            'LOGOUT': 'auth.logout',
            'REGISTER': 'common.register',
            'LOGIN': 'common.login',
            'BACK': 'common.back',
            'INFINITE': 'modes.infinite',
            'CHALLENGES': 'modes.challenges',
            'ALL GAMES': 'modes.allGames',
            'GUEST ACCOUNT: REGISTER TO SAVE YOUR PROGRESS!': 'ui.guestAccountLabel',
            "I don't have an account, start playing": 'auth.noAccountStart',
            'Code copied!': 'notifications.codeCopied',
            'Unlocked!': 'notifications.unlocked',
            'Not enough coins!': 'notifications.notEnoughCoins',
            'CHALLENGE FAILED': 'notifications.challengeFailed',
            'Score & Code copied to clipboard!': 'notifications.scoreCopied',
            'SURVIVE!': 'microgames.survive',
            'READY?': 'microgames.ready',
            'Please enter email and password': 'errors.loginRequiredFields',
            'All fields are required': 'errors.allFieldsRequired',
            'Passwords do not match': 'errors.passwordMismatch',
            'Please login first!': 'errors.loginFirst',
            'Please Login First!': 'errors.loginFirst',
            'CREATING GUEST ACCOUNT...': 'notifications.creatingGuest',
            'FAILED TO CREATE GUEST. TRY LOGIN?': 'notifications.guestCreationFailed',
            'Verifying Purchase...': 'notifications.verifyingPurchase',
            'PREMIUM ACTIVATED! INFINITE ENERGY!': 'notifications.premiumActivated',
            'Purchase Canceled.': 'notifications.purchaseCanceled',
            'INFINITE MODE': 'modes.infiniteLabel',
            'CHALLENGE RUN': 'challenges.runTitle',
            'BACK TO MENU': 'challenges.backToMenu',
            'NEXT': 'common.next'
        };

        if (directMap[source]) return this.t(directMap[source]);

        let match = source.match(/^\+(\d+)\s+COINS!\s*💰?$/);
        if (match) return this.t('notifications.coinsAdded', { amount: match[1] });

        match = source.match(/^CHALLENGE COMPLETE!\s+(\d+)\s+STAR\(S\)!$/);
        if (match) return this.t('notifications.challengeComplete', { stars: match[1] });

        match = source.match(/^RUN ENDED\. SCORE:\s*(\d+)$/);
        if (match) return this.t('notifications.runEndedScore', { score: match[1] });

        match = source.match(/^NO ENERGY LEFT!/);
        if (match) return this.t('notifications.noEnergyLeft');

        return text;
    }

    translateNotificationMessage(message) {
        if (typeof message !== 'string') return message;
        return this.translateKnownText(message);
    }

    translateInstructionText(text) {
        if (typeof text !== 'string') return text;
        return this.translateKnownText(text);
    }

    observeInstruction(element) {
        if (!element) return;
        if (this.instructionObserver) this.instructionObserver.disconnect();

        this.instructionObserver = new MutationObserver(() => {
            if (element.children.length > 0) return;
            const translated = this.translateInstructionText(element.textContent);
            if (translated !== element.textContent) {
                element.textContent = translated;
            }
        });

        this.instructionObserver.observe(element, {
            childList: true,
            subtree: true,
            characterData: true
        });
    }
}
